import { useCallback, useState } from 'react';
import { useRootDispatch, useRootSelector } from '../../../../store/hooks';
import { useInfiniteScroll, UseInfiniteScrollProps } from '../../../../hooks';
import { selectPageConfig } from '../../../../store/features/pageConfig';
import {
  fetchSiteArticlesData,
  selectSiteArticlesList,
  SiteArticles,
  updatePreloadedArticlesList,
} from '../../../../store/features/siteArticles';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { CardAnchor } from '../../../hub/shared/productFilter/cards/components';
import { Loader } from '../../../hub/shared/productFilter/products';
import Image from '../../../hub/shared/productFilter/cards/ArticleBlogCard/components/Image';
import Topics from '../../../hub/shared/productFilter/cards/ArticleBlogCard/components/Topics';
import Title from '../../../hub/shared/productFilter/cards/ArticleBlogCard/components/Title';
import Description from '../../../hub/shared/productFilter/cards/ArticleBlogCard/components/Description';
import ProductBlogCard from '../../../hub/shared/productFilter/products/cardBuilders/ProductBlogCard';

import styles from './ArticlesList.module.scss';

export default function ArticlesList() {
  const dispatch = useRootDispatch();
  const { sitePath } = useRootSelector(selectPageConfig);
  const articles = useRootSelector(selectSiteArticlesList);

  const [totalPage, setTotalPage] = useState<number>(100);

  const loadMoreArticlesHandler: UseInfiniteScrollProps['fetchMoreData'] =
    useCallback(
      (nextPageToLoad) => {
        return dispatch(
          fetchSiteArticlesData({
            queries: {
              page: nextPageToLoad,
              pageSize: 20,
            },
          }),
        ).then(({ payload, meta: { requestStatus } }) => {
          if (requestStatus === 'fulfilled') {
            const {
              data: { news = [] },
            } = payload as SiteArticles;

            if (news.length === 0) {
              setTotalPage(nextPageToLoad);
              return;
            }

            dispatch(updatePreloadedArticlesList(payload as SiteArticles));
          }
        });
      },
      [dispatch],
    );

  const { containerRef, loading } = useInfiniteScroll<HTMLDivElement>({
    fetchMoreData: loadMoreArticlesHandler,
    totalPage: totalPage,
    offsetContainerHeight: 300,
  });

  if (articles.length === 0) {
    return null;
  }

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <div className="container">
        <div className="row">
          {articles.map((article) => {
            const tags = article.tags.slice(0, 2);
            const path = article.externalLinkEnabled
              ? article.externalLink
              : `/article/${article.path}`;

            return (
              <div className="col-xs-12" key={article.id}>
                <div className={styles.card}>
                  <CardAnchor to={path} pathPrefix={sitePath}>
                    <ProductBlogCard
                      variant="list"
                      image={<Image path={article.imageUrl} />}
                      topics={tags.length > 0 ? <Topics list={tags} /> : null}
                      heading={
                        <Title>
                          {article.title}{' '}
                          {article.externalLinkEnabled && (
                            <OpenInNewIcon
                              fontSize="inherit"
                              sx={{ verticalAlign: 'middle' }}
                            />
                          )}
                        </Title>
                      }
                      description={
                        article.summary && (
                          <Description>{article.summary}</Description>
                        )
                      }
                    />
                  </CardAnchor>
                </div>
              </div>
            );
          })}
        </div>

        {loading && (
          <div className={styles.loader}>
            <Loader />
          </div>
        )}
      </div>
    </div>
  );
}
